<script setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useUsersStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import { emailValidator, requiredValidator } from '@validators'

const { dialogWidth, smAndDown } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
const {
  isEditingPersonalAdmins, personalAdmins, personalAdminsLoading, myUser,
} = storeToRefs(usersStore)

const isPlatinum = computed(() => !!myUser.value?.isPlatinum)
const newEmail = ref('')
const inviteError = ref('')
const inviting = ref(false)
const form = ref()
const showRemoveConfirm = ref({})

const activeAdmins = computed(() => personalAdmins.value.filter(pa => pa.status === 'active'))
const pendingAdmins = computed(() => personalAdmins.value.filter(pa => pa.status === 'pending'))

const requestClose = () => {
  newEmail.value = ''
  inviteError.value = ''
  usersStore.hidePersonalAdminsDialog()
}

const sendInvite = async () => {
  inviteError.value = ''
  const { valid } = await form.value.validate()
  if (!valid) return
  inviting.value = true
  try {
    await usersStore.invitePersonalAdmin(newEmail.value.trim().toLowerCase())
    newEmail.value = ''

    // Without resetValidation(), requiredValidator re-fires on the cleared model.
    form.value?.resetValidation()
  } catch (err) {
    inviteError.value = err?.response?.data?.error || 'Could not send invitation'
  } finally {
    inviting.value = false
  }
}

const revoke = async email => {
  try {
    await usersStore.revokePersonalAdmin(email)
    showRemoveConfirm.value[email] = false
  } catch (err) {
    inviteError.value = err?.response?.data?.error || 'Could not revoke admin'
  }
}
</script>

<template>
  <VDialog
    :width="smAndDown ? undefined : dWidth"
    :model-value="isEditingPersonalAdmins"
    persistent
    :fullscreen="smAndDown"
    scrollable
  >
    <VCard class="admin-dialog">
      <VToolbar
        color="primary"
        class="admin-dialog__toolbar"
      >
        <VToolbarTitle class="text-body-1 font-weight-medium">
          Admin Account <span class="admin-dialog__beta">(beta)</span>
        </VToolbarTitle>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          color="white"
          @click="requestClose"
        />
      </VToolbar>

      <div class="admin-dialog__body">
        <div
          v-if="!isPlatinum"
          class="admin-dialog__section"
        >
          <p class="text-body-2">
            Admin accounts are a complimentary Platinum benefit. Upgrade your membership to add an admin to your account.
          </p>
        </div>

        <template v-else>
          <div class="admin-dialog__section">
            <p class="admin-dialog__intro text-body-2 mb-3">
              Admin accounts can submit reservation requests &amp; group/private dining inquiries on your behalf, edit your profile details, upload receipts, connect you with friends, and message restaurants on your behalf.
            </p>
            <p class="admin-dialog__intro text-body-2 mb-0">
              They cannot change your password or email or delete your account.
            </p>
          </div>

          <div class="admin-dialog__section">
            <h4 class="admin-dialog__heading">
              Invite an admin
            </h4>
            <VForm
              ref="form"
              @submit.prevent="sendInvite"
            >
              <div class="invite-row">
                <VTextField
                  v-model="newEmail"
                  label="Admin email"
                  type="email"
                  placeholder="admin@example.com"
                  :rules="[requiredValidator, emailValidator]"
                  density="comfortable"
                  hide-details="auto"
                  class="invite-email"
                />
                <VBtn
                  type="submit"
                  color="primary"
                  size="large"
                  :loading="inviting"
                  :block="smAndDown"
                >
                  Send invite
                </VBtn>
              </div>
              <p
                v-if="inviteError"
                class="text-error text-body-2 mt-2"
              >
                {{ inviteError }}
              </p>
            </VForm>
          </div>

          <div class="admin-dialog__section">
            <h4 class="admin-dialog__heading">
              Pending invitations
            </h4>
            <p
              v-if="personalAdminsLoading"
              class="text-disabled text-body-2"
            >
              Loading…
            </p>
            <p
              v-else-if="pendingAdmins.length === 0"
              class="text-disabled text-body-2"
            >
              No pending invitations.
            </p>
            <div
              v-for="admin in pendingAdmins"
              :key="admin.email"
              class="admin-row"
            >
              <div class="admin-row__info">
                <strong>{{ admin.email }}</strong>
                <span class="text-disabled text-caption">Awaiting acceptance</span>
              </div>
              <VBtn
                variant="text"
                color="error"
                size="small"
                @click="revoke(admin.email)"
              >
                Cancel
              </VBtn>
            </div>
          </div>

          <div class="admin-dialog__section">
            <h4 class="admin-dialog__heading">
              Active admin accounts
            </h4>
            <p
              v-if="activeAdmins.length === 0"
              class="text-disabled text-body-2"
            >
              No active admin accounts.
            </p>
            <div
              v-for="admin in activeAdmins"
              :key="admin.email"
              class="admin-row"
            >
              <div class="admin-row__info">
                <strong>{{ admin.email }}</strong>
              </div>
              <div
                v-if="showRemoveConfirm[admin.email]"
                class="confirm-row"
              >
                <span class="text-body-2">Remove access?</span>
                <VBtn
                  variant="text"
                  size="small"
                  @click="showRemoveConfirm[admin.email] = false"
                >
                  Cancel
                </VBtn>
                <VBtn
                  color="error"
                  size="small"
                  @click="revoke(admin.email)"
                >
                  Remove
                </VBtn>
              </div>
              <VBtn
                v-else
                variant="text"
                color="error"
                size="small"
                @click="showRemoveConfirm[admin.email] = true"
              >
                Remove
              </VBtn>
            </div>
          </div>
        </template>
      </div>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.admin-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.admin-dialog__toolbar {
  flex: 0 0 auto;
}

.admin-dialog__beta {
  font-size: 12px;
  font-weight: 400;
  opacity: 0.75;
  margin-left: 4px;
}

.admin-dialog__body {
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0;
}

.admin-dialog__section {
  padding: 16px 20px;

  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
}

.admin-dialog__intro {
  margin: 0;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.5;
}

.admin-dialog__heading {
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
}

.invite-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.invite-email {
  flex: 1 1 220px;
  min-width: 0;

  // iOS won't zoom on focus when input is >= 16px (CLAUDE.md rule).
  :deep(input) {
    font-size: 16px;
  }
}

.admin-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 0;

  & + .admin-row {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
}

.admin-row__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  word-break: break-all;
  flex: 1 1 200px;
}

.confirm-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
